import { useEffect, useState } from "react";
import Dropdown from "react-bootstrap/Dropdown";
import { UiComponents, UnoStoreHooks } from "@simulanisindia/ui";
import { CheckIcon, CopyIcon, CopyLinkIcon, GlobeIcon, LockIcon, PlusIcon } from "../../assets/icons/icons.js";
import Skeleton from "react-loading-skeleton";
import apiclient from "../../apiclient/index.js";
import { PROJECT_SHARE, PROJECT_SHARE_LIST } from "../../apiclient/apiEndpoints.js";
import { getPlaceholderName } from "../../utils/general.js";
import BtnLoad from "../common/BtnLoad.js";
import useDebounce from "../../hooks/useDebounce.js";

const { SearchBar } = UiComponents;
const { useUnoSelector } = UnoStoreHooks;

const accessLabelMap = {
  VIEWER: "Read",
  EDITOR: "Edit",
  OWNER: "Owner",
  REMOVE: "Remove",
};
const accessTypes = ["VIEWER", "EDITOR", "REMOVE"];

const accessControls = [
  { name: "Public", access: "Public", desc: "Anyone with the link can view" },
  { name: "Private", access: "Private", desc: "Only people with access can view" },
];

const ShareModal = ({ details, onHide, onOpenEditModal }) => {
  const configData = useUnoSelector((state) => state.config);
  const userData = useUnoSelector((state) => state.user);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [shareList, setShareList] = useState([]);
  const [dataLoad, setDataLoad] = useState(true);
  const [docAccess, setDocAccess] = useState("");
  const [textCopied, setTextCopied] = useState(false);
  const getProjectShareList = async () => {
    setDataLoad(true);
    try {
      let _projectId = details?.uuid || null;
      if (!_projectId) return;
      let data = await apiclient.get(`${configData.config.API_URL}${PROJECT_SHARE_LIST}/${_projectId}`);
      setShareList(data?.usersList);
      setDocAccess(data?.projectDetails?.public ? "Public" : "Private");
    } catch (error) {
      console.log(error);
    } finally {
      setDataLoad(false);
    }
  };

  useEffect(() => {
    getProjectShareList();
  }, [details]);

  const handleAccessChange = (userUUID, newRole) => {
    setShareList((prev) =>
      prev.map((user) =>
        user.uuid === userUUID
          ? {
              ...user,
              edit: true,
              allocation: { ...user.allocation, type: newRole },
            }
          : user
      )
    );
  };

  const handleUpdateRole = async () => {
    let _projectId = details?.uuid || null;
    if (loading || !_projectId) return;
    setLoading(true);
    try {
      let editUser = shareList
        ?.filter((e) => e.edit === true)
        ?.map((f) => ({ id: f.uuid, type: f.allocation.type.toLowerCase(), categoryType: f.categoryType }));

      let bodyData = {
        data: [...editUser],
        docAccess: docAccess === "Public",
      };
      await apiclient.post(`${configData.config.API_URL}${PROJECT_SHARE}/${_projectId}`, bodyData);
      getProjectShareList();
      onHide();
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  let searchValue = useDebounce(search, 500);
  const _sharedList = shareList?.filter((user) => user.name.toLowerCase().includes(searchValue.toLowerCase()));

  return (
    <>
      <div className="share-modal">
        <div className="modal-head">
          <h6>Share "{details?.name || ""}"</h6>
          <div className="search-parent">
            <SearchBar placeholder="Search ..." value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
        </div>

        <div className="modal-body">
          <div className="access">
            <div className="d-flex align-items-center justify-content-between">
              <h6>Who has access</h6>
              <span className="addMore" onClick={onOpenEditModal}>
                <h6>
                  {" "}
                  <PlusIcon /> Add People
                </h6>
              </span>
            </div>
            <div className="user-list">
              {dataLoad ? (
                <UserLoadItem />
              ) : _sharedList?.length === 0 ? (
                <NoUserFound searchValue={searchValue} />
              ) : (
                _sharedList?.map((user, index) => {
                  const currentAccess = user.allocation.type;
                  if (user.allocation.type === "OWNER") {
                    return (
                      <div className="user-item" key={index}>
                        <div className="user-info">
                          <UserAvatar user={user} />
                          <span className="name-email">
                            <p className="name">{user.name} (You)</p>
                            <p className="email">{user.email || user.uid}</p>
                          </span>
                        </div>
                        <div className="dropdown-here">
                          <p className="owner"> Owner </p>
                        </div>
                      </div>
                    );
                  } else {
                    return (
                      <div className="user-item" key={index}>
                        <div className={`user-info  ${user.allocation.type === "REMOVE" && "remove"}`}>
                          <UserAvatar user={user} />
                          <span className="name-email">
                            <p className="name">{user.name}</p>
                            <p className="email">{user.email || user.uid}</p>
                          </span>
                        </div>
                        <div className="dropdown-here">
                          <Dropdown>
                            <Dropdown.Toggle disabled={user.uuid === userData.user.uuid ? true : false}>
                              {accessLabelMap[currentAccess]}
                            </Dropdown.Toggle>
                            <Dropdown.Menu>
                              {accessTypes?.map((access, i) => (
                                <Dropdown.Item key={i} onClick={() => handleAccessChange(user.uuid, access)}>
                                  <span className="option">
                                    <span className="checked">{access === currentAccess && <CheckIcon />}</span>
                                    <span>{accessLabelMap[access] || "Remove"}</span>
                                  </span>
                                </Dropdown.Item>
                              ))}
                            </Dropdown.Menu>
                          </Dropdown>
                        </div>
                      </div>
                    );
                  }
                })
              )}
            </div>
          </div>
          <div className="access limited">
            <h6>General Access</h6>
            <div className="gen-dropdown">
              <span className="gen-icon">{docAccess == "Private" ? <LockIcon /> : <GlobeIcon />}</span>
              <div className="drop-info">
                <div className="dropdown-here">
                  <Dropdown className="">
                    <Dropdown.Toggle>{docAccess}</Dropdown.Toggle>
                    <Dropdown.Menu>
                      {accessControls?.map((ele) => (
                        <div className="options" key={ele.access}>
                          <Dropdown.Item onClick={() => setDocAccess(ele.access)}>
                            <div className="option">
                              <span> {ele.name}</span>
                              {docAccess === ele.access && (
                                <span className="checked">
                                  <CheckIcon />
                                </span>
                              )}
                            </div>
                          </Dropdown.Item>
                        </div>
                      ))}
                    </Dropdown.Menu>
                  </Dropdown>
                </div>
                <p> {accessControls?.find((ele) => ele.access === docAccess)?.desc} </p>
              </div>
            </div>
          </div>
          <div className="share-footer">
            <div className="d-flex gap-2">
              <button
                className="link copy"
                onClick={() => {
                  navigator.clipboard.writeText(`${window.location.origin}/view/${details?.uuid}`);
                  setTextCopied((prev) => !prev);
                  setTimeout(() => setTextCopied((prev) => !prev), 2000);
                }}
              >
                {/* <button className="link copy" onClick={onOpenEditModal}> */}
                {!textCopied ? (
                  <>
                    <CopyIcon />
                    Copy Link{" "}
                  </>
                ) : (
                  "Copied!"
                )}
              </button>
              {/* <button className="link" onClick={onOpenEditModal}>
                <PlusIcon />
                Add More
              </button> */}
            </div>
            <button className="save" onClick={handleUpdateRole}>
              {loading ? <BtnLoad /> : "Save"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default ShareModal;

export const UserAvatar = ({ user }) => {
  const [imageError, setImageError] = useState(false);
  return (
    <>
      {!imageError && user.avatar ? (
        <div className="thumnail shareUser">
          <img
            className="smallavatar "
            src={user.avatar}
            alt={user.name}
            onError={() => setImageError(true)}
            referrerPolicy="no-referrer"
          />
        </div>
      ) : (
        <span className="init">{getPlaceholderName(user.name)}</span>
      )}
    </>
  );
};

export const UserLoadItem = ({ count = 4 }) => {
  return [...Array(count)].map((_, index) => {
    return (
      <div className="user-item">
        <div className="user-info w-100">
          <Skeleton width="3rem" height="3rem" circle />
          <span className="name-email w-100">
            <p className="name w-50">
              <Skeleton width="100%" height="1rem" />
            </p>
            <p className="email w-100">
              <Skeleton width="100%" height="1rem" />
            </p>
          </span>
        </div>
      </div>
    );
  });
};

export const NoUserFound = ({ searchValue, editShareModal }) => {
  return (
    <div className="empty-state">
      {editShareModal ? (
        <p>
          No {editShareModal === "users" ? "users" : "groups"} found for <strong>"{searchValue}"</strong>.
        </p>
      ) : (
        <p>
          No users or groups found for <strong>"{searchValue}"</strong>.
        </p>
      )}
      {!editShareModal && (
        <p>
          Try searching again or use the <strong>"Add More"</strong> option at the bottom to add a new user or group.
        </p>
      )}
    </div>
  );
};
